<template lang="pug">
el-form(
  ref="refForm"
  :model="item"
  status-icon
  label-position="top"
).c-invite-detail-form
  app-main-content-layout(title='Invitation Detail' back-to='/manage/invites')
    template(#actions v-if="!item.invite_accepted")
      el-button(@click="handleResend()" v-loading="resending").has-fai.button-green.min-w-110
        fai(type="fal" icon="sync").size-18
        span Resend Invitation
      el-button(type="danger" plain @click="handleRemove()" v-loading="removing").has-fai.min-w-110
        fai(type="fal" icon="trash").size-18
        span Delete Invitation

    template(#col-left)
      .c-content-section
        el-form-item(label="Organization Name")
          el-input(v-model="item.name" disabled)

        el-form-item(label="Email Address")
          el-input(v-model="item.email" disabled)

        el-form-item(label="Invitation Code" v-if="item.invite_code")
          el-input(v-model="item.invite_code" disabled)

      .c-content-section.mt-3(v-if="item.invite_accepted")
        .invite-status-accepted
          .status-icon
            fai(type="fal" icon="check-circle").size-32.text-success
          .status-content
            h5.mb-1 Invitation Accepted
            p.mb-0.text-muted This invitation has been accepted and the organization is now active.

    template(#col-right)
      .c-content-section
        el-form-item(label="Invitation Status")
          .status-badge(:class="inviteStatusClass")
            | {{ inviteStatusText }}

        el-form-item(label="Invitation Stage" v-if="item.invite_stage")
          el-input(v-model="item.invite_stage" disabled)

        el-form-item(label="Created Date" v-if="item.createdAt")
          el-input(:value="formatDate(item.createdAt)" disabled)
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  reactive,
  onMounted,
  computed,
} from 'vue';
import dayjs from 'dayjs';
import { ElMessage } from 'element-plus';
import shared from './shared';

const itemData = {
  id: -1,
  name: '',
  email: '',
  phone: '',
  invite_code: '',
  invite_accepted: false,
  invite_stage: '',
  createdAt: '',
};

export default defineComponent({
  name: 'InviteDetail',
  setup() {
    const {
      route,
      router,
      resendInvite,
      removeInvite,
      removeItemGetConfirm,
      doGetInvite,
    } = shared();
    const { inviteId } = route.params;
    const item = reactive(itemData);
    const resending = ref(false);
    const removing = ref(false);

    // Computed properties for status display
    const inviteStatusText = computed(() => {
      if (item.invite_accepted) {
        return 'Accepted';
      }
      return 'Pending';
    });

    const inviteStatusClass = computed(() => {
      if (item.invite_accepted) {
        return 'status-badge--active';
      }
      return 'status-badge--invited';
    });

    // Format date function
    const formatDate = (dateString: string) => {
      if (!dateString) return '';
      return dayjs(dateString).format('MM/DD/YYYY');
    };

    onMounted(async () => {
      const response = await doGetInvite(inviteId);
      Object.assign(item, response);
    });

    async function handleResend() {
      try {
        resending.value = true;
        await resendInvite(item.id);
        ElMessage({
          message: 'Invitation resent successfully',
          type: 'success',
        });
      } catch (error) {
        console.error('Error resending invitation:', error);
        ElMessage({
          message: 'Failed to resend invitation',
          type: 'error',
        });
      } finally {
        resending.value = false;
      }
    }

    function handleRemove() {
      removeItemGetConfirm(async () => {
        try {
          await removeInvite(item.id);
          ElMessage({
            message: 'Invitation deleted successfully',
            type: 'success',
          });
          router.push('/manage/invites');
        } catch (error) {
          console.error('Error deleting invitation:', error);
          ElMessage({
            message: 'Failed to delete invitation',
            type: 'error',
          });
        }
      });
    }

    return {
      resending,
      removing,
      refForm: ref(null),
      item,
      handleRemove,
      handleResend,
      inviteStatusText,
      inviteStatusClass,
      formatDate,
    };
  },
});
</script>

<style lang="scss" scoped>
// Color Variables - Match UsersEdit.vue
$color-teal-accent: #01beb0;
$gray-50: #f9fafb;
$gray-100: #f3f4f6;
$gray-200: #e5e7eb;
$gray-300: #d1d5db;
$gray-400: #9ca3af;
$gray-500: #6b7280;
$gray-600: #4b5563;
$gray-700: #374151;
$gray-900: #111827;
$color-purple-text: #4349dd;

// Main Form Container
.c-invite-detail-form {
  padding: 2rem;
  
  :deep(.grid-2-cols) {
    gap: 1.5rem;
  }
  
  :deep(.col-has-child) {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
}

// White Content Sections
.c-content-section {
  background: white;
  border-radius: 16px;
  border: 1px solid #dfdfdf;
  box-shadow: none;
  padding: 1.5rem;
  overflow: hidden;
}

// Form Styling
:deep(.el-form-item) {
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }

  .el-form-item__label {
    color: $gray-700;
    font-weight: 600;
    font-size: 0.875rem;
    line-height: 1.5;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .el-input__wrapper {
    border-radius: 10px;
    border: 1.5px solid $gray-200;
    background: #F0F0F3;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
    padding: 0 12px;
    height: 40px;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

    &.is-disabled {
      border: 1.5px solid $gray-200;
      background: #F0F0F3;
      opacity: 0.7;
    }
  }

  .el-input__inner {
    height: 100%;
    font-size: 0.875rem;
    color: $gray-700;
    background: transparent;
    font-weight: 500;
  }
}

// Button Styling
:deep(.el-button) {
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  height: 36px;
  padding: 0 20px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border-width: 1px;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 0.5rem;

  > span {
    display: inline-flex;
    align-items: center;
    line-height: 1;
  }

  .fai,
  svg {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    margin-right: 0.5rem;
  }

  &.min-w-110 {
    min-width: 110px;
  }

  &.button-green {
    background-color: $color-teal-accent;
    border-color: $color-teal-accent;
    color: white;

    &:hover {
      background-color: color.adjust($color-teal-accent, $lightness: -5%);
      border-color: color.adjust($color-teal-accent, $lightness: -5%);
      box-shadow: 0 2px 4px 0 rgba(1, 190, 176, 0.2);
      transform: translateY(-1px);
    }

    &:active {
      background-color: color.adjust($color-teal-accent, $lightness: -8%);
      border-color: color.adjust($color-teal-accent, $lightness: -8%);
      transform: translateY(0);
      box-shadow: 0 1px 2px 0 rgba(1, 190, 176, 0.2);
    }
  }

  &.el-button--danger {
    &.is-plain {
      background-color: white;
      border-color: #f56c6c;
      color: #f56c6c;

      &:hover {
        background-color: #fef0f0;
        border-color: #f56c6c;
        color: #f56c6c;
        box-shadow: 0 2px 4px 0 rgba(245, 108, 108, 0.2);
        transform: translateY(-1px);
      }

      &:active {
        transform: translateY(0);
      }
    }
  }
}

// Status Badge Styling
.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.375rem 0.875rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.5;
  white-space: nowrap;
  border: 1px solid;

  &--active {
    background-color: #D1F4F0;
    color: #0D9488;
    border-color: #14B8A6;
  }

  &--invited {
    background-color: #E0E7FF;
    color: #4F46E5;
    border-color: #818CF8;
  }
}

// Invite Status Accepted Section
.invite-status-accepted {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background-color: #D1F4F0;
  border: 1px solid #14B8A6;
  border-radius: 12px;

  .status-icon {
    display: flex;
    align-items: center;
    justify-content: center;

    .size-32 {
      font-size: 32px;
    }

    .text-success {
      color: #0D9488;
    }
  }

  .status-content {
    flex: 1;

    h5 {
      font-size: 1rem;
      font-weight: 600;
      color: $gray-900;
      margin-bottom: 0.25rem;
    }

    p {
      font-size: 0.875rem;
      color: $gray-600;
      margin-bottom: 0;
    }
  }
}

// Text Utilities
.text-muted {
  color: $gray-500;
}

.mb-0 {
  margin-bottom: 0;
}

.mb-1 {
  margin-bottom: 0.25rem;
}

.mt-3 {
  margin-top: 1rem;
}

// Page Background
:deep(.app-main-content-layout) {
  background-color: $gray-50;
}

// Responsive Design
@media (max-width: 768px) {
  .c-invite-detail-form {
    padding: 1.5rem;
  }

  .c-content-section {
    padding: 1.25rem;
    border-radius: 12px;
  }

  :deep(.grid-2-cols) {
    gap: 1rem;
  }

  :deep(.el-form-item) {
    margin-bottom: 1.25rem;

    .el-form-item__label {
      font-size: 0.8125rem;
    }

    .el-input__inner {
      font-size: 0.8125rem;
    }
  }

  :deep(.el-button) {
    padding: 8px 16px;
    font-size: 0.8125rem;
  }

  .invite-status-accepted {
    flex-direction: column;
    text-align: center;
  }
}

@media (max-width: 576px) {
  .c-invite-detail-form {
    padding: 1rem;
  }

  .c-content-section {
    padding: 1rem;
    border-radius: 12px;
  }
}
</style>
